import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { QRCodeSVG } from 'qrcode.react';

const StaffQRGenerator = ({ staffMembers, onQRGenerated }) => {
  const { currentTenant, isGymOwner } = useAuth();
  const [selectedStaff, setSelectedStaff] = useState('');
  const [generatedQR, setGeneratedQR] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleGenerateQR = async () => {
    if (!selectedStaff) {
      setError('Please select a staff member');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setGeneratedQR(null);

    try {
      // Check if staff already has an active QR code
      const { data: existingQR, error: checkError } = await supabase?.from('staff_qr_codes')?.select('*')?.eq('tenant_id', currentTenant?.id)?.eq('user_id', selectedStaff)?.eq('is_active', true)?.single();

      if (existingQR) {
        setGeneratedQR(existingQR);
        setSuccess('Staff member already has an active QR code');
        setLoading(false);
        return;
      }

      // Generate new QR code
      const qrData = `STAFF-${currentTenant?.id?.substring(0, 8)}-${selectedStaff?.substring(0, 8)}-${Date.now()}`;

      const { data, error } = await supabase?.from('staff_qr_codes')?.insert({
          tenant_id: currentTenant?.id,
          user_id: selectedStaff,
          qr_code_data: qrData,
          is_active: true,
          generated_at: new Date()?.toISOString()
        })?.select()?.single();

      if (error) throw error;

      setGeneratedQR(data);
      setSuccess('QR code generated successfully!');
      onQRGenerated?.();
    } catch (error) {
      console.error('Error generating QR code:', error);
      setError('Failed to generate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateQR = async (qrId) => {
    if (!confirm('Are you sure you want to deactivate this QR code?')) return;

    try {
      const { error } = await supabase?.from('staff_qr_codes')?.update({ is_active: false })?.eq('id', qrId);

      if (error) throw error;

      setSuccess('QR code deactivated successfully');
      setGeneratedQR(null);
      onQRGenerated?.();
    } catch (error) {
      console.error('Error deactivating QR code:', error);
      setError('Failed to deactivate QR code');
    }
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('staff-qr-code');
    const svgData = new XMLSerializer()?.serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas?.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img?.width;
      canvas.height = img?.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas?.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      const staffMember = staffMembers?.find(s => s?.user_id === selectedStaff);
      downloadLink.download = `staff-qr-${staffMember?.user_profiles?.full_name?.replace(/\s+/g, '-')}.png`;
      downloadLink.href = pngFile;
      downloadLink?.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const selectedStaffMember = staffMembers?.find(s => s?.user_id === selectedStaff);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Generation Form */}
      <div className="bg-card rounded-lg shadow-md p-6 border border-border">
        <div className="flex items-center gap-2 mb-6">
          <Icon name="QrCode" size={24} className="text-primary" />
          <h2 className="text-xl font-bold text-foreground">Generate Staff QR Code</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Staff Member
            </label>
            <Select
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e?.target?.value)}
            >
              <option value="">Choose a staff member...</option>
              {staffMembers?.map((staff) => (
                <option key={staff?.user_id} value={staff?.user_id}>
                  {staff?.user_profiles?.full_name} ({staff?.role})
                </option>
              ))}
            </Select>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-sm text-green-500">{success}</p>
            </div>
          )}

          <Button
            onClick={handleGenerateQR}
            disabled={loading || !selectedStaff}
            className="w-full"
          >
            {loading ? 'Generating...' : 'Generate QR Code'}
          </Button>
        </div>

        {/* Staff List with QR Status */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Staff QR Code Status</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {staffMembers?.map((staff) => {
              const hasActiveQR = staff?.staff_qr_codes?.some(qr => qr?.is_active);
              return (
                <div
                  key={staff?.user_id}
                  className="p-3 bg-background rounded-lg border border-border"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">{staff?.user_profiles?.full_name}</p>
                      <p className="text-xs text-muted-foreground">{staff?.role}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      hasActiveQR 
                        ? 'bg-green-500/10 text-green-500' :'bg-gray-500/10 text-gray-500'
                    }`}>
                      {hasActiveQR ? 'Active QR' : 'No QR'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* QR Code Display */}
      <div className="bg-card rounded-lg shadow-md p-6 border border-border">
        {generatedQR ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground">Generated QR Code</h3>
              <Button
                onClick={() => handleDeactivateQR(generatedQR?.id)}
                variant="outline"
                size="sm"
                className="text-red-500 hover:text-red-600"
              >
                Deactivate
              </Button>
            </div>

            {selectedStaffMember && (
              <div className="p-4 bg-background rounded-lg border border-border">
                <p className="text-sm text-muted-foreground mb-1">Staff Member</p>
                <p className="font-semibold text-foreground">{selectedStaffMember?.user_profiles?.full_name}</p>
                <p className="text-xs text-muted-foreground">{selectedStaffMember?.role}</p>
              </div>
            )}

            <div className="flex justify-center p-6 bg-white rounded-lg">
              <QRCodeSVG
                id="staff-qr-code"
                value={generatedQR?.qr_code_data}
                size={256}
                level="H"
                includeMargin={true}
              />
            </div>

            <div className="space-y-2">
              <Button
                onClick={handleDownloadQR}
                className="w-full flex items-center justify-center gap-2"
              >
                <Icon name="Download" size={18} />
                Download QR Code
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Staff can use this QR code for check-in, lunch breaks, and check-out
              </p>
            </div>

            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-blue-500 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-500 mb-1">How to Use</p>
                  <ul className="text-xs text-foreground space-y-1">
                    <li>• Scan QR code at arrival to check in</li>
                    <li>• Scan again when starting lunch break</li>
                    <li>• Scan when returning from lunch</li>
                    <li>• Scan at departure to check out</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-12">
            <Icon name="QrCode" size={64} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No QR Code Generated</p>
            <p className="text-sm mt-2">Select a staff member and generate their QR code</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffQRGenerator;